import { RolodexDocumentEntry, useDocumentSignedUrls } from 'features/files'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import { usePdfAsImage } from 'features/knowledgeDocumentViewer/components/viewer/hooks/usePdfAsImage'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

interface UseDocumentThumbnailOptions {
  document: DocumentTableRow | null
  scale?: number
}

interface UseDocumentThumbnailResult {
  thumbnailUrl: string | null
  isLoading: boolean
  isPdf: boolean
  isImage: boolean
}

export const useDocumentThumbnail = ({
  document,
  scale = 1,
}: UseDocumentThumbnailOptions): UseDocumentThumbnailResult => {
  const rolodexDocument: RolodexDocumentEntry | null = useMemo(() => {
    if (!document) return null
    return {
      type: RolodexEntryType.REPORT,
      entry_id: document.id,
      filename: document.filename || document.title,
      file_type: document.filename.split('.').pop()?.toLowerCase() || '',
      size: 0,
      categories: [],
      fields: {},
      tags: {},
    }
  }, [document])

  const {
    signedUrl,
    isLoading: isUrlLoading,
    isPdf,
    isImage,
  } = useDocumentSignedUrls(rolodexDocument)

  const pdfUrl = isPdf ? signedUrl : undefined
  const { imageUrl: pdfImageUrl, isLoading: isPdfLoading } = usePdfAsImage(
    pdfUrl,
    1,
    scale
  )

  const thumbnailUrl = useMemo(() => {
    if (isPdf && pdfImageUrl) {
      return pdfImageUrl
    }
    if (isImage && signedUrl) {
      return signedUrl
    }
    return null
  }, [isPdf, pdfImageUrl, isImage, signedUrl])

  const isLoading = isUrlLoading || (isPdf && isPdfLoading)

  return {
    thumbnailUrl,
    isLoading,
    isPdf,
    isImage,
  }
}
